import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { useAuthActions } from "@convex-dev/auth/react";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { navigateBasedOnRole } from "@/lib/auth/role-redirect";
import { getSecureSignInErrorMessage } from "@/lib/errors/auth-errors";
import { useToastAction } from "@/hooks/useToastAction";
import {
  AuthLeftPanel,
  AuthRightPanel,
  AuthMobileLogo,
} from "@/components/auth/auth-layout";

const getSignInErrorMessage = (error: any): string => {
  return getSecureSignInErrorMessage(error);
};

const signInSearchSchema = z.object({
  message: z.string().optional(),
});

export const Route = createFileRoute("/_public/auth/sign-in")({
  validateSearch: signInSearchSchema,
  component: SignInPage,
});

function SignInPage() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  const { message } = Route.useSearch();
  const currentUser = useQuery(api.system.users.queries.getCurrentUser);

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [savedEmail, setSavedEmail] = useState("");

  useEffect(() => {
    if (message === "account-configured") {
      toast.success(
        "Cuenta configurada exitosamente. Ahora puedes iniciar sesión.",
      );
    } else if (message === "password-reset") {
      toast.success(
        "Contraseña restablecida exitosamente. Inicia sesión con tu nueva contraseña.",
      );
    }
  }, [message]);

  useEffect(() => {
    const remembered = localStorage.getItem("rememberedEmail");
    if (remembered) {
      setSavedEmail(remembered);
      setRememberMe(true);
    }
  }, []);

  const loginAction = async (
    e: React.FormEvent<HTMLFormElement>,
    shouldRemember: boolean,
  ) => {
    const formData = new FormData(e.currentTarget);
    const email = (formData.get("email") as string).toLowerCase().trim();
    formData.set("email", email);
    formData.append("flow", "signIn");

    await signIn("password", formData);

    if (shouldRemember) {
      localStorage.setItem("rememberedEmail", email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }
  };

  const { execute: login, isLoading: isLoginLoading } = useToastAction(
    loginAction,
    {
      loadingMessage: "Iniciando sesión...",
      successMessage: undefined,
      errorMessage: getSignInErrorMessage,
    },
  );

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await login(e, rememberMe);
  };

  useEffect(() => {
    if (currentUser) {
      navigateBasedOnRole(currentUser, navigate);
    }
  }, [currentUser, navigate]);

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <AuthLeftPanel
        headline="Del paquete al destino, sin perder el rastro"
        subtitle="Gestiona paquetes, consolida envíos, rastrea entregas y genera reportes desde un solo lugar. Todo conectado, en tiempo real."
        badge="Logística para negocios inteligentes"
      />

      <AuthRightPanel>
        <div className="mb-8 text-center lg:text-left">
          <AuthMobileLogo />
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            Bienvenido de vuelta
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Ingresa tus credenciales para acceder a tu cuenta
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="tu@email.com"
              defaultValue={savedEmail}
              required
              disabled={isLoginLoading}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Contraseña</Label>
              <Link
                to="/auth/reset-password"
                className="text-xs text-primary hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="********"
                required
                disabled={isLoginLoading}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 z-10 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoginLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={isLoginLoading}
            />
            <Label
              htmlFor="rememberMe"
              className="text-sm font-normal cursor-pointer"
            >
              Recordarme
            </Label>
          </div>

          <Button type="submit" className="w-full" disabled={isLoginLoading}>
            {isLoginLoading ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Iniciando sesión...
              </>
            ) : (
              <>
                Iniciar sesión
                <ArrowRight className="ml-2 size-4" />
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 space-y-3 text-center text-sm">
          <div className="text-muted-foreground">
            ¿No tienes cuenta?{" "}
            <Link
              to="/auth/sign-up"
              className="text-primary hover:underline font-medium"
            >
              Registrate
            </Link>
          </div>
          <div className="text-muted-foreground">
            ¿Primera vez?{" "}
            <Link
              to="/auth/first-time-login"
              className="text-primary hover:underline font-medium"
            >
              Configura tu cuenta
            </Link>
          </div>
        </div>
      </AuthRightPanel>
    </div>
  );
}
